"use client";

import * as React from "react";
import * as RTabs from "@radix-ui/react-tabs";
import { cn } from "./cn";

export type TabsProps = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (v: string) => void;
  className?: string;
  children: React.ReactNode;
};

export function Tabs({
  value,
  defaultValue,
  onValueChange,
  className,
  children,
}: TabsProps) {
  return (
    <RTabs.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      className={className}
    >
      {children}
    </RTabs.Root>
  );
}

export function TabsList({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <RTabs.List
      className={cn(
        "inline-flex items-center gap-1 bg-mist rounded-full p-1 overflow-x-auto",
        className,
      )}
    >
      {children}
    </RTabs.List>
  );
}

export function TabsTrigger({
  value,
  className,
  children,
  badge,
}: {
  value: string;
  className?: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <RTabs.Trigger
      value={value}
      className={cn(
        "h-9 px-4 rounded-full text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors",
        "text-ash hover:text-ink",
        "data-[state=active]:bg-ink data-[state=active]:text-paper",
        className,
      )}
    >
      {children}
      {badge != null ? (
        <span
          className={cn(
            "h-5 min-w-[20px] px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold",
            "bg-smoke text-ash",
            "group-data-[state=active]:bg-white/20 group-data-[state=active]:text-paper",
          )}
        >
          {badge}
        </span>
      ) : null}
    </RTabs.Trigger>
  );
}

export function TabsContent({
  value,
  className,
  children,
}: {
  value: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <RTabs.Content value={value} className={cn("mt-5 outline-none", className)}>
      {children}
    </RTabs.Content>
  );
}
